import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { StatusBadge, PriorityBadge, SlaBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { fmtDate } from "../lib/format";
import { Ticket, CircleDot, Loader2, CheckCircle2, Clock, PlusCircle } from "lucide-react";

function StatCard({ label, value, icon: Icon, accent, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-border bg-card p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
        <Icon className={`h-4 w-4 ${accent}`} />
      </div>
      <div className="font-heading font-black tracking-tighter text-4xl">{value}</div>
    </div>
  );
}

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { data: stats } = useQuery({ queryKey: ["dashboard"], queryFn: () => api.get("/dashboard").then((r) => r.data) });
  const { data: tickets, isLoading } = useQuery({ queryKey: ["my-tickets"], queryFn: () => api.get("/tickets").then((r) => r.data) });

  return (
    <Layout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading font-black tracking-tighter text-4xl">My Dashboard</h1>
          <p className="text-muted-foreground mt-1">Overview of your support tickets</p>
        </div>
        <Button onClick={() => navigate("/tickets/new")} data-testid="new-ticket-btn" className="gap-2">
          <PlusCircle className="h-4 w-4" /> New Ticket
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard testid="stat-total" label="Total" value={stats?.total ?? "—"} icon={Ticket} accent="text-primary" />
        <StatCard testid="stat-open" label="Open" value={stats?.open ?? "—"} icon={CircleDot} accent="text-slate-500" />
        <StatCard testid="stat-inprogress" label="In Progress" value={stats?.in_progress ?? "—"} icon={Loader2} accent="text-blue-500" />
        <StatCard testid="stat-resolved" label="Resolved" value={stats?.resolved ?? "—"} icon={Clock} accent="text-teal-500" />
        <StatCard testid="stat-done" label="Done" value={stats?.done ?? "—"} icon={CheckCircle2} accent="text-emerald-500" />
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="px-5 py-4 border-b border-border font-heading font-bold tracking-tight">Recent Tickets</div>
        {isLoading ? (
          <div className="p-10 text-center text-muted-foreground">Loading…</div>
        ) : tickets?.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-muted-foreground mb-4">You haven't created any tickets yet.</p>
            <Button onClick={() => navigate("/tickets/new")} data-testid="empty-new-ticket">Create your first ticket</Button>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {tickets?.slice(0, 6).map((t) => (
              <Link key={t.id} to={`/tickets/${t.id}`} data-testid={`ticket-row-${t.ticket_number}`} className="flex items-center gap-4 px-5 py-4 hover:bg-secondary/60 transition-colors">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono-plex text-xs text-muted-foreground">{t.ticket_number}</span>
                    <PriorityBadge priority={t.priority} />
                  </div>
                  <div className="font-medium truncate mt-1">{t.subject}</div>
                </div>
                <div className="hidden sm:block w-32">
                  <Progress value={t.progress} className="h-1.5" />
                  <div className="text-[10px] text-muted-foreground mt-1">{t.progress}%</div>
                </div>
                <StatusBadge status={t.status} statusMeta={t.status_meta} />
                <SlaBadge status={t.sla.status} />
                <span className="hidden md:block text-xs text-muted-foreground w-20 text-right">{fmtDate(t.created_at)}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
